import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Slider,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

// A naive color-threshold-based background removal
// This won't work for complex images or multiple background colors.
// For a professional, production-level approach, consider a dedicated
// background removal API like remove.bg or a custom model.
function naiveBackgroundRemoval(ctx, width, height, threshold) {
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  // Let's use the top-left pixel as "background color" reference (R, G, B)
  const rBg = data[0];
  const gBg = data[1];
  const bBg = data[2];

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    // Compute difference
    const diff = Math.sqrt(
      Math.pow(r - rBg, 2) + Math.pow(g - gBg, 2) + Math.pow(b - bBg, 2)
    );

    // If pixel is close to background color, make it transparent
    if (diff < threshold) {
      data[i + 3] = 0; // Set alpha to 0
    }
  }

  ctx.putImageData(imageData, 0, 0);
}

const ImageFilterEditor = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [editedImage, setEditedImage] = useState(null);

  // Filter states
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [grayscale, setGrayscale] = useState(0);
  const [saturate, setSaturate] = useState(100);
  const [removeBackground, setRemoveBackground] = useState(false);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setOriginalImage(e.target.result);
      setEditedImage(null);
    };
    reader.readAsDataURL(file);
  };

  const applyFilters = () => {
    if (!originalImage) return;

    const img = new Image();
    img.src = originalImage;

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;

      // 1) Draw image normally first
      ctx.drawImage(img, 0, 0, img.width, img.height);

      // 2) If removeBackground, do naive removal
      if (removeBackground) {
        // For this naive example, use a threshold of ~80
        naiveBackgroundRemoval(ctx, img.width, img.height, 80);
      }

      // 3) Apply filters via CSS filter approach:
      // We can either do direct pixel manipulations,
      // but CSS filters for a final rendering are simpler. For the final
      // output image, let's do them in a separate canvas pass to ensure
      // we can download them properly.

      // We'll do a second canvas to apply filters:
      const filteredCanvas = document.createElement("canvas");
      const filteredCtx = filteredCanvas.getContext("2d");
      filteredCanvas.width = img.width;
      filteredCanvas.height = img.height;

      // Instead of strictly using ctx.filter, let's do the re-draw
      // within the filter context:
      filteredCtx.filter = `
        brightness(${brightness}%)
        contrast(${contrast}%)
        grayscale(${grayscale}%)
        saturate(${saturate}%)
      `;

      // Now copy from the first canvas
      filteredCtx.drawImage(canvas, 0, 0);

      // Convert final to dataURL
      const finalDataURL = filteredCanvas.toDataURL("image/png");
      setEditedImage(finalDataURL);
    };
  };

  useEffect(() => {
    // Re-apply filters anytime the values or the removeBackground toggle changes
    if (originalImage) {
      applyFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brightness, contrast, grayscale, saturate, removeBackground]);

  const handleDownload = () => {
    if (!editedImage) return;
    const link = document.createElement("a");
    link.href = editedImage;
    link.download = "edited-image.png";
    link.click();
  };

  const handleResetAll = () => {
    setOriginalImage(null);
    setEditedImage(null);
    setBrightness(100);
    setContrast(100);
    setGrayscale(0);
    setSaturate(100);
    setRemoveBackground(false);
  };

  return (
    <Container maxWidth="md" sx={{ mt: 2 }}>
      <Box
        sx={{
          mb: 3,
          py: 2,
          textAlign: "center",
          backgroundColor: "#f2f2f2",
        }}
      >
        <Typography variant="h4" gutterBottom align="center" sx={{ mb: 1 }}>
          Image Filter & Editor
        </Typography>
      </Box>
      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        {/* File Upload */}
        <Box
          sx={{
            border: "2px dashed #ccc",
            borderRadius: 2,
            height: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          {originalImage ? (
            <img
              src={originalImage}
              alt="Original"
              style={{ maxHeight: "100%", objectFit: "contain" }}
            />
          ) : (
            <Typography color="text.secondary" variant="body2">
              No image selected
            </Typography>
          )}
        </Box>

        <Button
          variant="outlined"
          color="primary"
          startIcon={<UploadFileIcon />}
          component="label"
          sx={{ borderRadius: 2, mb: 3 }}
        >
          Choose File
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileUpload}
          />
        </Button>

        {/* Filters Section */}
        {originalImage && (
          <>
            <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Adjust Filters
              </Typography>

              {/* Brightness */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Brightness
                </Typography>
                <Slider
                  value={brightness}
                  onChange={(e, val) => setBrightness(val)}
                  min={0}
                  max={200}
                  step={1}
                />
              </Box>

              {/* Contrast */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Contrast
                </Typography>
                <Slider
                  value={contrast}
                  onChange={(e, val) => setContrast(val)}
                  min={0}
                  max={200}
                  step={1}
                />
              </Box>

              {/* Grayscale */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Grayscale
                </Typography>
                <Slider
                  value={grayscale}
                  onChange={(e, val) => setGrayscale(val)}
                  min={0}
                  max={100}
                  step={1}
                />
              </Box>

              {/* Saturation */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Saturation
                </Typography>
                <Slider
                  value={saturate}
                  onChange={(e, val) => setSaturate(val)}
                  min={0}
                  max={200}
                  step={1}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Background Removal */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={removeBackground}
                    onChange={(e) => setRemoveBackground(e.target.checked)}
                  />
                }
                label="Remove Background (Naive)"
              />
            </Paper>

            {/* Preview and Download */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              <Card
                sx={{
                  borderRadius: 2,
                  width: { xs: "100%", md: "45%" },
                }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Edited Preview
                  </Typography>
                  <Box
                    sx={{
                      border: "2px dashed #ccc",
                      borderRadius: 2,
                      height: 200,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    {editedImage ? (
                      <img
                        src={editedImage}
                        alt="Edited Preview"
                        style={{ maxHeight: "100%", objectFit: "contain" }}
                      />
                    ) : (
                      <Typography color="text.secondary" variant="body2">
                        No edits yet
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleDownload}
                  disabled={!editedImage}
                  startIcon={<DownloadIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Download
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleResetAll}
                  startIcon={<RestartAltIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Reset All
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default ImageFilterEditor;

/*
# Created/Modified files during execution:
ImageFilterEditor.jsx
*/
